<template>
    <dual-select
        :inputLabel="label"
        name="relations"
        labelKey="value"
        :options="availableOptions"
        @input="handleInput"
        v-bind="$attrs"
        v-if="multiple"
    />
    <div v-else class="row" :class="{ 'has-danger' : error !== null, 'form-group': !noFormGroup }">
        <label :class="labelClasses" class="form-control-label col-form-label">{{ computedLabel }}</label>
        <div :class="inputHolderClasses">
            <multi-select
                :options="availableOptions"
                :loading="availableOptions.length === 0"
                label="value"
                :selectedLabel="uLang('app.select')"
                :placeholder="uLang('app.select_an_option')"
                :deselectLabel="uLang('app.press_enter_to_remove')"
                :selectLabel="uLang('app.press_enter_to_select')"
                v-bind="$attrs"
                :value="internalValue"
                @input="handleInput"
            />
            <span
                class="help-block form-control-feedback"
                v-if="error != null"
                v-text="error"
            ></span>
        </div>
    </div>
</template>

<script>
    import {DualSelect} from '~/Form/elements';
    import MultiSelect from 'vue-multiselect'

    export default {
        name: 'setting-select',

        components: {
            DualSelect,
            MultiSelect,
        },

        props: {
            setting: {
                type: String,
                required: true,
            },
            multiple: {
                type: Boolean,
                default: false,
            },
            inputHolderClasses: {
                type: String,
                required: false,
                default: 'col-md-9 col-xl-10 col-sm-12'
            },
            labelClasses: {
                type: String,
                required: false,
                default: 'col-md-3 col-xl-2 col-sm-12',
            },
            noFormGroup: {
                type: Boolean,
                required: false,
                default: false,
            },
            error: {
                type: String,
                default: null,
            },
            label: {
                type: String,
                default: null,
            },
            required: {
                type: Boolean,
                default: false,
            },
            value: {
                required: false,
                default: null,
            },

            /**
             * Whether or not we want to return the full 'option' object,
             * instead of just the 'id'
             */
            returnFullObject: {
                type: Boolean,
                default: false,
            }
        },

        data() {
            return {
                availableOptions: [],
                internalValue: null,
            }
        },

        computed: {
            // Display the label + asterisk for required
            computedLabel() {
                return (null !== this.label ? uLang(this.label) : '') + ': ' + (this.required ? '*' : '');
            }
        },

        watch: {
            /**
             * Watch the value we get passed into the settings select.
             * Whenever we get a new value, we need to set our internal value according to this value
             */
            value: {
                immediate: true,
                handler: 'setInternalValue',
            },
        },

        mounted() {
            this.$store.dispatch('refreshSettingValuesFor', this.setting).then((data) => {
                this.availableOptions = this.$store.getters.settingValuesFor({setting: this.setting});
            });
        },

        methods: {
            setInternalValue(newValue, oldValue, tries = 0) {
                if (newValue === null) {
                    this.internalValue = null;
                    return this.$emit('input', this.internalValue);
                }

                // Try 10 times to set our internal value, with 500ms in between.
                // If we haven't yet gotten our availableOptions from the store,
                // The options will not be available yet, so we can't transform.
                if (this.availableOptions.length === 0 && tries < 10) {
                    return setTimeout(() => {
                        this.setInternalValue(newValue, oldValue, tries++)
                    }, 500);
                }

                // Transform the value we got passed as a prop, to the proper internal value
                this.internalValue = this.normalizeToObjects(newValue);

                if (this.returnFullObject) {
                    return this.$emit('input', this.internalValue);
                }

                if (this.multiple) {
                    return this.$emit('input', this.internalValue.map(object => object.id));
                }

                this.$emit('input', this.internalValue.id);
            },

            /**
             * Handle a change by the user, coming from the multi-select or dual-select
             */
            handleInput(event) {
                this.internalValue = this.transformValue(event);

                this.$emit('input', this.internalValue);
            },

            /**
             * Transforms the value into a full object if necessary
             */
            transformValue(value) {
                /**
                 * Handle if we're a multiselect
                 * (event will be an array of id's)
                 */
                if (true === this.multiple && true === this.returnFullObject) {
                    // Convert array of id's into an array of objects.
                    return value.map((id) => {
                        return this.availableOptions.find((option) => {
                            return id === option.id;
                        });
                    });
                }

                /**
                 * Handle tranformation to id for 'single' select
                 * (Event will be an object).
                 */
                if (false === this.multiple && false === this.returnFullObject) {
                    return $getIfExists(value, 'id', null);
                }

                return value;
            },

            /**
             * Transform the id into a full object of necessary
             */
            normalizeToObjects(value) {
                // If its an object, return
                if (typeof value === 'object') {
                    return value;
                }

                // If we only have one value, transform the id into an object
                if (! this.multiple) {
                    return this.availableOptions.find((option) => {
                        return value === option.id;
                    });
                }

                // If we have multiple values, transform all of them into objects
                if (this.multiple) {
                    // If all are objects already, just return
                    if (value.every((item) => typeof item === 'object')) {
                        return value;
                    }

                    return value.map((id) => {
                        if (typeof id === 'object') {
                            return id;
                        }

                        return this.availableOptions.find((option) => {
                            return id === option.id;
                        });
                    });
                }
            }
        }
    }
</script>

<style lang="scss"></style>
